// SCR-STAFF-932 — برچسب‌های منبع (US-ORG-012): برچسب‌های سیستمی فقط‌خواندنی (AC-ORG-012.1) و سفارشی با ایجاد، ویرایش و حذف؛
// حذف برچسب استفاده‌شده در الزامات خدمت با فهرست خدمات وابسته رد می‌شود (AC-ORG-012.2).
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type ResourceTagView } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  ListPage,
  TextFieldRHF,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { orgKeys, TAG_CODE_PATTERN, useResourceTags, useTagLabel } from './common';

export const SCR_STAFF_932 = 'SCR-STAFF-932';

export function ResourceTagsPage() {
  const { t } = useTranslation('org');
  const write = useWriteAccess('org.room.manage');
  const tags = useResourceTags();
  const tagLabel = useTagLabel();
  const [editing, setEditing] = useState<ResourceTagView | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<ResourceTagView | null>(null);

  const system = (tags.data?.items ?? []).filter((tag) => tag.isSystem);
  const custom = (tags.data?.items ?? []).filter((tag) => !tag.isSystem);

  const baseColumns: DataTableColumn<ResourceTagView>[] = [
    {
      id: 'code',
      headerKey: 'org:resource_tags.columns.code',
      cell: (row) => <bdi dir="ltr">{row.code}</bdi>,
    },
    { id: 'name', headerKey: 'org:resource_tags.columns.name', cell: (row) => tagLabel(row) },
    {
      id: 'rooms',
      headerKey: 'org:resource_tags.columns.rooms',
      cell: (row) => faDigits(Number(row.roomCount)),
    },
  ];
  const systemColumns: DataTableColumn<ResourceTagView>[] = [
    ...baseColumns,
    {
      id: 'actions',
      headerKey: 'org:resource_tags.columns.actions',
      cell: (row) => (
        <Chip
          size="small"
          variant="outlined"
          icon={<LockOutlinedIcon fontSize="small" />}
          label={t('org:resource_tags.readonly')}
          data-testid={`tag-readonly-${row.code}`}
        />
      ),
    },
  ];
  const customColumns: DataTableColumn<ResourceTagView>[] = [
    ...baseColumns,
    {
      id: 'actions',
      headerKey: 'org:resource_tags.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('org:resource_tags.edit')}>
            <span>
              <IconButton
                aria-label={`${t('org:resource_tags.edit')} ${row.code}`}
                onClick={() => setEditing(row)}
                disabled={!write.canWrite}
                data-testid={`tag-edit-${row.code}`}
              >
                <EditOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('org:resource_tags.delete')}>
            <span>
              <IconButton
                aria-label={`${t('org:resource_tags.delete')} ${row.code}`}
                onClick={() => setDeleting(row)}
                disabled={!write.canWrite}
                data-testid={`tag-delete-${row.code}`}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_932}
      titleKey="org:resource_tags.title"
      subtitleKey="org:resource_tags.subtitle"
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setEditing(null)}
          disabled={!write.canWrite}
          data-testid="tag-create"
        >
          {t('org:resource_tags.add')}
        </Button>
      }
      filters={<WriteNotice write={write} />}
    >
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography variant="h6" component="h2">
            {t('org:resource_tags.system_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('org:resource_tags.system_hint')}
          </Typography>
          <DataTable
            captionKey="org:resource_tags.caption_system"
            columns={systemColumns}
            rows={tags.data ? system : undefined}
            getRowId={(row) => row.id}
            isLoading={tags.isPending}
            error={tags.error}
            onRetry={() => void tags.refetch()}
          />
        </Stack>
        <Stack spacing={1}>
          <Typography variant="h6" component="h2">
            {t('org:resource_tags.custom_title')}
          </Typography>
          <DataTable
            captionKey="org:resource_tags.caption_custom"
            columns={customColumns}
            rows={tags.data ? custom : undefined}
            getRowId={(row) => row.id}
            isLoading={tags.isPending}
            error={tags.error}
            onRetry={() => void tags.refetch()}
            emptyTitleKey="org:resource_tags.empty_custom"
          />
        </Stack>
      </Stack>
      {editing !== undefined && <TagFormDialog tag={editing} onClose={() => setEditing(undefined)} />}
      {deleting && <DeleteTagDialog tag={deleting} onClose={() => setDeleting(null)} />}
    </ListPage>
  );
}

const tagSchema = z.object({
  code: z.string().regex(TAG_CODE_PATTERN, { error: 'errors:validation.org_tag_code' }),
  name: zRequiredText(100),
});
type TagForm = z.input<typeof tagSchema>;

function TagFormDialog({ tag, onClose }: { tag: ResourceTagView | null; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const creating = tag === null;
  const values = useMemo<TagForm>(() => ({ code: tag?.code ?? '', name: tag?.name ?? '' }), [tag]);
  const { control, handleSubmit, formState, setError } = useForm<TagForm>({
    resolver: zodResolver(tagSchema),
    defaultValues: values,
  });

  const save = useMutation({
    mutationFn: (form: TagForm) =>
      creating
        ? unwrap(
            tenantApi.POST('/api/v1/organization/resource-tags', {
              body: { code: form.code.trim(), name: form.name },
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
          )
        : unwrap(
            tenantApi.PUT('/api/v1/organization/resource-tags/{id}', {
              params: { path: { id: tag.id } },
              body: { code: form.code.trim(), name: form.name, rowVersion: tag.rowVersion },
            }),
          ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => {
      if (creating) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orgKeys.tags(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'name'],
        codeFields: { 'PHX-ORG-0401': 'code' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-932-FORM"
      titleKey={creating ? 'org:resource_tags.create_title' : 'org:resource_tags.edit_title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <TagUsageAlert error={submitError} />
      <TextFieldRHF
        control={control}
        name="code"
        labelKey="org:resource_tags.form.code"
        helperKey="org:resource_tags.form.code_help"
        required
        fullWidth
        slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'tag-code' } }}
      />
      <TextFieldRHF
        control={control}
        name="name"
        labelKey="org:resource_tags.form.name"
        required
        fullWidth
        slotProps={{ htmlInput: { 'data-testid': 'tag-name' } }}
      />
    </FormDialog>
  );
}

/** فهرست خدمات وابسته (PHX-ORG-0403) یا تعداد اتاق‌ها (PHX-ORG-0404). */
function TagUsageAlert({ error }: { error: unknown }) {
  const { t } = useTranslation('org');
  if (!isApiError(error)) {
    return null;
  }
  if (error.code === 'PHX-ORG-0403') {
    const services =
      (error.extensions['services'] as { serviceId: string; name: string }[] | undefined) ?? [];
    return (
      <Alert severity="warning" data-testid="tag-in-use">
        {t('org:resource_tags.in_use')}
        <List dense>
          {services.map((service) => (
            <ListItem key={service.serviceId} data-testid="tag-in-use-service">
              {service.name}
            </ListItem>
          ))}
        </List>
      </Alert>
    );
  }
  if (error.code === 'PHX-ORG-0404') {
    return (
      <Alert severity="warning" data-testid="tag-assigned">
        {t('org:resource_tags.assigned', { n: faDigits(Number(error.extensions['rooms'] ?? 0)) })}
      </Alert>
    );
  }
  return null;
}

function DeleteTagDialog({ tag, onClose }: { tag: ResourceTagView; onClose: () => void }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const titleId = useId();
  const tagLabel = useTagLabel();

  const remove = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.DELETE('/api/v1/organization/resource-tags/{id}', { params: { path: { id: tag.id } } }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orgKeys.tags(session?.tenantId) });
      onClose();
    },
  });
  const known = isApiError(remove.error) && ['PHX-ORG-0403', 'PHX-ORG-0404'].includes(remove.error.code);

  return (
    <Dialog
      open
      onClose={remove.isPending ? undefined : onClose}
      aria-labelledby={titleId}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id={titleId}>
        {t('org:resource_tags.delete_dialog.title', { name: tagLabel(tag) })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>{t('org:resource_tags.delete_dialog.message')}</DialogContentText>
          <TagUsageAlert error={remove.error} />
          {remove.error && !known ? <ErrorState error={remove.error} compact /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={remove.isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => remove.mutate()}
          disabled={remove.isPending || known}
          data-testid="tag-delete-confirm"
        >
          {t('org:resource_tags.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
